import { z } from 'zod';

export const roles = [
  'admin',
  'operation',
  'consulting',
  'external',
  'content',
  'system',
  'finance',
  'super_finance',
  // Marketing HUB (XT-808) — coarse portal access for the marketing team.
  // The hub's own roles (admin | marketing | member | rep) live in
  // marketing.access_role and are enforced inside the hub.
  'marketing_admin',
  // Eleggia DMC Partner Profile roles (apps/auth/prd/dmc-partner-profile.md §2).
  // Govern field-level L1/L2/L3 access to the DMC partner profile.
  'hq_admin',
  'hq_finance_legal',
  'dmc_principal',
  'dmc_editor',
  'consortium_member',
] as const;

export const roleSchema = z.enum(roles);
export type Role = z.infer<typeof roleSchema>;

// Named constants for in-code use — prefer `Role.Admin` over the literal
// `'admin'` so renames are mechanical and TS will catch typos.
// Keys are PascalCase per project convention; values match the
// persisted DB enum (lowercase / snake_case).
export const Role = {
  Admin: 'admin',
  Operation: 'operation',
  Consulting: 'consulting',
  External: 'external',
  Content: 'content',
  System: 'system',
  Finance: 'finance',
  SuperFinance: 'super_finance',
  MarketingAdmin: 'marketing_admin',
  // Eleggia DMC roles.
  HqAdmin: 'hq_admin',
  HqFinanceLegal: 'hq_finance_legal',
  DmcPrincipal: 'dmc_principal',
  DmcEditor: 'dmc_editor',
  ConsortiumMember: 'consortium_member',
} as const satisfies Record<string, (typeof roles)[number]>;

export const accountStatuses = ['Active', 'Invited', 'Suspended'] as const;
export const accountStatusSchema = z.enum(accountStatuses);
export type AccountStatus = z.infer<typeof accountStatusSchema>;

export const AccountStatus = {
  Active: 'Active',
  Invited: 'Invited',
  Suspended: 'Suspended',
} as const satisfies Record<
  (typeof accountStatuses)[number],
  (typeof accountStatuses)[number]
>;

export const resources = [
  'dashboard',
  'itineraries',
  'products',
  // Product Activity Log (XT-519). A resource of its own rather than another
  // `products` action: read access to the audit trail is granted separately
  // from read access to the product, and export is narrower still (§16).
  'product_activity',
  'operations',
  'crm',
  'inbox',
  'tasks',
  'incidents',
  'support_tickets',
  'notifications',
  'user_settings',
  'company_settings',
  'dmc_profile',
  'recommendation',
  'agent-panel',
  'accounts',
  'whatsapp_messages',
  'api_monitoring',
  // Stakeholder system brief (XT-507). Its own resource rather than an admin
  // role check: the brief is handed to individual stakeholders who must NOT
  // get the rest of the admin surface with it, so the grant has to be
  // revocable on its own.
  'system_brief',
  // Travel-agent portal itinerary list (M38). Its own resource rather than
  // reusing `itineraries:read`: that grant is held by the whole cms-public
  // storefront SSR surface, and this list must be reachable ONLY from the
  // agent portal. Deliberately NOT granted to the main API's service account
  // (apps/api/src/shared/auth-http.ts) so the procedure fails closed if it is
  // ever mounted on `appRouter`.
  'agent_portal',
  // Payment logs (XT-620). Split out of `itineraries` because the finance
  // team must be able to patch a payment row (v2 let anyone who reached
  // /payment-logs do so) WITHOUT gaining `itineraries:update`, which guards
  // ~55 procedures across 13 routers — itinerary edit, items, price lines,
  // versions. Same rationale as `product_activity` / `system_brief`: the
  // grant has to be revocable on its own.
  'payment_logs',
  // Marketing HUB (XT-808). Coarse nav/API gate separate from hub roles
  // (admin|marketing|member|rep in marketing.access_role).
  'marketing',
] as const;

export const resourceSchema = z.enum(resources);
export type Resource = z.infer<typeof resourceSchema>;

export const actions = [
  'create',
  'read',
  'update',
  'delete',
  'use',
  'send',
  // Export-to-file. Separated from `read` so an audit trail can be readable
  // without being extractable (XT-519 §16).
  'export',
  // The INTERNAL view of a resource, as distinct from the storefront-safe one.
  // Separated from `read` because the two differ in what a free-text query may
  // reach and which rows are visible at all: on products, the internal view
  // also ILIKEs supplier names and labels, and does not exclude hidden rows.
  // A caller without this grant gets the public projection — the default is
  // the narrow one, so a credential that was never considered fails closed.
  'read_internal',
] as const;
export const actionSchema = z.enum(actions);
export type Action = z.infer<typeof actionSchema>;

// Named constants for in-code use — prefer `Resource.Itineraries` /
// `Action.Read` over the literal `'itineraries'` / `'read'` so renames are
// mechanical and TS will catch typos. Keys are PascalCase per project
// convention; values match the persisted strings.
export const Resource = {
  Dashboard: 'dashboard',
  Itineraries: 'itineraries',
  Products: 'products',
  ProductActivity: 'product_activity',
  Operations: 'operations',
  Crm: 'crm',
  Inbox: 'inbox',
  Tasks: 'tasks',
  Incidents: 'incidents',
  SupportTickets: 'support_tickets',
  Notifications: 'notifications',
  UserSettings: 'user_settings',
  CompanySettings: 'company_settings',
  DmcProfile: 'dmc_profile',
  Recommendation: 'recommendation',
  AgentPanel: 'agent-panel',
  Accounts: 'accounts',
  WhatsappMessages: 'whatsapp_messages',
  ApiMonitoring: 'api_monitoring',
  SystemBrief: 'system_brief',
  AgentPortal: 'agent_portal',
  PaymentLogs: 'payment_logs',
  Marketing: 'marketing',
} as const satisfies Record<string, (typeof resources)[number]>;

export const Action = {
  Create: 'create',
  Read: 'read',
  ReadInternal: 'read_internal',
  Update: 'update',
  Delete: 'delete',
  Use: 'use',
  Send: 'send',
  Export: 'export',
} as const satisfies Record<string, (typeof actions)[number]>;

export const possessions = ['own', 'any'] as const;
export type Possession = (typeof possessions)[number];

export const authUserSchema = z.object({
  id: z.uuid(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  roles: z.array(roleSchema),
  permissions: z.array(z.string()),
  organizationId: z.uuid().nullable(),
  status: accountStatusSchema,
  // The user's office IANA timezone — the operations DateCell office-time
  // badge shows an item's start time in this zone when the item sits in a
  // different one. Optional so existing fixtures/callers stay valid.
  timezone: z.string().optional(),
  // Profile-tab fields. Surfaced on `auth.me` so the Personal profile editor
  // can hydrate the form without an admin-only `accounts.byId` round-trip.
  // Optional so existing fixtures/callers (operations, dashboard, etc.) that
  // never touch these stay valid.
  phone: z.string().nullable().optional(),
  paymentZoneId: z.uuid().nullable().optional(),
  // Set while the user still holds an admin-issued temporary password. Every
  // authenticated procedure but the two that let them resolve it is refused
  // until they set their own (XT-716). Optional so existing fixtures/callers
  // stay valid.
  mustChangePasswordSince: z.string().nullable().optional(),
});

export type AuthUser = z.infer<typeof authUserSchema>;

// Org context returned alongside `auth.me` so the frontend can avoid a
// second round-trip to `organizations.list`. Mirrors legacy
// auth.service.ts:56-95 (organizationName, currency, supportPhone, etc.).
export const meOrganizationContextSchema = z.object({
  id: z.uuid(),
  title: z.string().nullable(),
  supportPhoneNumber: z.string().nullable(),
  logoKey: z.string().nullable(),
  currency: z.string().nullable(),
  subdomain: z.string().nullable(),
  settings: z.record(z.string(), z.unknown()).nullable(),
});

export type MeOrganizationContext = z.infer<typeof meOrganizationContextSchema>;

export const meResponseSchema = z.object({
  user: authUserSchema,
  organization: meOrganizationContextSchema.nullable(),
  // Whether file uploads are configured on this deployment (UPLOADTHING_TOKEN
  // set). Lets the client hide attachment affordances instead of hitting a 503.
  uploadsEnabled: z.boolean().optional(),
  // Whether Jira is configured (JIRA_* set). Lets the incidents management area
  // hide the "Post to Jira" action when there's no Jira to post to.
  jiraEnabled: z.boolean().optional(),
});

export type MeResponse = z.infer<typeof meResponseSchema>;

export const signOutInput = z.object({
  sessionId: z.string(),
});

export const permissionsInput = z.object({
  roles: z.array(roleSchema).optional(),
});
